import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  EnvelopeIcon, 
  ArrowPathIcon, 
  CheckCircleIcon,
  ArrowLeftIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const OTPVerification = ({ email, onVerify, onResend, onBack, loading }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }

    // Start timer
    const interval = setInterval(() => {
      setTimer((prevTimer) => {
        if (prevTimer <= 1) {
          setCanResend(true);
          clearInterval(interval);
          return 0;
        }
        return prevTimer - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleChange = (index, value) => {
    if (isNaN(value)) return; // Only allow numbers

    const newOtp = [...otp];
    newOtp[index] = value.slice(0, 1); // Take only first character
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }

    // If all digits are filled, auto-submit
    if (index === 5 && value && newOtp.every(digit => digit !== '')) {
      handleSubmit(newOtp.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
    // Handle arrow keys
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1].focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const digits = pastedData.split('');
      const newOtp = [...otp];
      digits.forEach((digit, index) => {
        if (index < 6) newOtp[index] = digit;
      });
      setOtp(newOtp);
      
      // Focus the next empty input or last input
      const nextEmptyIndex = newOtp.findIndex(d => d === '');
      if (nextEmptyIndex !== -1) {
        inputRefs.current[nextEmptyIndex].focus();
      } else {
        inputRefs.current[5].focus();
      }
    }
  };

  const handleSubmit = (otpCode) => {
    setIsVerifying(true);
    onVerify(otpCode);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="max-w-md w-full bg-white p-10 rounded-2xl shadow-xl"
    >
      {/* Back button */}
      <button
        onClick={onBack}
        className="mb-6 text-gray-400 hover:text-gray-600 transition-colors"
      >
        <ArrowLeftIcon className="h-5 w-5" />
      </button>

      {/* Header */}
      <div className="text-center mb-8">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="mx-auto h-16 w-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mb-4"
        >
          <EnvelopeIcon className="h-8 w-8 text-white" />
        </motion.div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Email</h2>
        <p className="text-gray-600">
          We've sent a verification code to
        </p>
        <p className="font-medium text-primary-600 mt-1">{email}</p>
      </div>

      {/* OTP Input Fields */}
      <div className="flex justify-center space-x-3 mb-6">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            pattern="\d*"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            className="w-14 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-all duration-200"
            disabled={loading || isVerifying}
          />
        ))}
      </div>

      {/* Timer */}
      <div className="flex items-center justify-center space-x-2 text-sm text-gray-500 mb-6">
        <ClockIcon className="h-4 w-4" />
        <span>Code expires in {formatTime(timer)}</span>
      </div>

      {/* Verify Button */}
      <button
        onClick={() => handleSubmit(otp.join(''))}
        disabled={otp.some(digit => !digit) || loading || isVerifying}
        className="w-full py-3 px-4 bg-gradient-to-r from-primary-600 to-primary-500 text-white font-medium rounded-lg hover:from-primary-700 hover:to-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 mb-4"
      >
        {isVerifying ? (
          <>
            <ArrowPathIcon className="animate-spin h-5 w-5 mx-auto" />
          </>
        ) : (
          'Verify Email'
        )}
      </button>

      {/* Resend Option */}
      <div className="text-center">
        <p className="text-sm text-gray-600 mb-2">Didn't receive the code?</p>
        {canResend ? (
          <button
            onClick={onResend}
            disabled={loading}
            className="text-primary-600 hover:text-primary-700 font-medium text-sm disabled:opacity-50"
          >
            Resend Code
          </button>
        ) : (
          <p className="text-sm text-gray-400">
            Resend available in {formatTime(timer)}
          </p>
        )}
      </div>

      {/* Security Note */}
      <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
        <p className="text-xs text-yellow-700 flex items-start">
          <CheckCircleIcon className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
          Never share this code with anyone. Our team will never ask for your verification code.
        </p>
      </div>
    </motion.div>
  );
};

export default OTPVerification;